import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignFremont = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Craftsman ADU Design & Construction in Fremont",
    "description": "Custom Craftsman ADUs in Fremont. Expert design & construction by UDA ADU. Authentic Craftsman architecture with modern functionality.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU"
    },
    "url": "https://www.udaadu.com/craftsman-adu-design-fremont"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/craftsman-adu-design-fremont" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/30min"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Craftsman ADU Design & Construction in Fremont
          </h1>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Embrace the timeless appeal of Craftsman architecture with a beautifully designed ADU in Fremont. Our authentic Craftsman designs celebrate quality craftsmanship, natural materials, and functional beauty that defines this beloved American architectural style.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Design Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/landing">Try UDA AI Designer</Link>
            </Button>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Authentic Craftsman ADU Features
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Low-Pitched Roofs</h3>
                  <p className="text-gray-200">Wide eaves and exposed rafters creating horizontal emphasis</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Natural Materials</h3>
                  <p className="text-gray-200">Wood siding, stone foundations, and authentic material combinations</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Covered Front Porches</h3>
                  <p className="text-gray-200">Welcoming porches with thick tapered columns and stone bases</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Handcrafted Details</h3>
                  <p className="text-gray-200">Built-in furniture, window boxes, and artisan woodwork</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Multi-Paned Windows</h3>
                  <p className="text-gray-200">Double-hung windows with decorative mullions and trim</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Craftsman ADU Benefits in Fremont
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Quality Craftsmanship</h3>
                <p className="text-gray-200">Built to last with attention to detail and superior materials</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Energy Efficiency</h3>
                <p className="text-gray-200">Natural materials and design promote comfortable living year-round</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Functional Beauty</h3>
                <p className="text-gray-200">Every element serves both aesthetic and practical purposes</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Market Appeal</h3>
                <p className="text-gray-200">Craftsman style remains highly desirable in the Bay Area</p>
              </div>
            </div>
          </div>
        </div>

        {/* Interior Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-white">
            Craftsman Interior Elements
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Built-in Cabinetry</h3>
              <p className="text-gray-200">Custom built-ins maximize storage while maintaining clean lines</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Exposed Beams</h3>
              <p className="text-gray-200">Structural elements become decorative features highlighting craftsmanship</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Warm Wood Tones</h3>
              <p className="text-gray-200">Rich oak, mahogany, and cedar create inviting, cozy atmospheres</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Tile Accents</h3>
              <p className="text-gray-200">Handcrafted tiles add color and texture to kitchens and baths</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Open Floor Plans</h3>
              <p className="text-gray-200">Connected living spaces promote family interaction and flow</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Window Seats</h3>
              <p className="text-gray-200">Cozy reading nooks and storage solutions built into the architecture</p>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-white">
            Fremont Craftsman ADU Design Process
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Craftsman Consultation</h3>
              <p className="text-gray-200">Explore authentic Craftsman elements for your Fremont property</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Authentic Design</h3>
              <p className="text-gray-200">Create true-to-style plans emphasizing natural materials</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Permit Process</h3>
              <p className="text-gray-200">Handle Fremont building requirements and approvals</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Master Construction</h3>
              <p className="text-gray-200">Skilled artisans bring Craftsman quality to life</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-3xl font-semibold mb-4 text-white">
            Build Your Craftsman Dream ADU in Fremont
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Create an authentic Craftsman ADU that celebrates quality craftsmanship and natural beauty. Our Fremont specialists will design and build a space that embodies the timeless appeal of the Arts and Crafts movement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Start Craftsman Project
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/adu-calculator">Get Craftsman Quote</Link>
            </Button>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4 text-white">Explore More Fremont ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/modern-farmhouse-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Modern Farmhouse ADUs Fremont
            </Link>
            <Link to="/contemporary-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Contemporary ADUs Fremont
            </Link>
            <Link to="/transitional-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Transitional ADUs Fremont
            </Link>
            <Link to="/spanish-colonial-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Spanish Colonial ADUs Fremont
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default CraftsmanADUDesignFremont;